import { AppBar, Box, Stack, type SxProps } from "@mui/material";
import { useCallback, useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { HeaderBarBurger } from "./header-bar-burger";

interface HeaderBarProps {
  navBurger?: boolean;
  sx?: SxProps;
  children?: ReactNode;
}

export const HeaderBar: FC<HeaderBarProps> = ({ navBurger, children, sx = [] }) => {
  const appBarRef = useRef<HTMLDivElement | null>(null);
  const [appBarHeight, setAppBarHeight] = useState(0);

  const measure = useCallback(() => {
    const el = appBarRef.current;
    if (!el) return;
    const h = el.offsetHeight;
    setAppBarHeight(h);
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(measure);

    const el = appBarRef.current;
    let ro: ResizeObserver | undefined;
    if (el && "ResizeObserver" in window) {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }

    const vv = window.visualViewport;
    vv?.addEventListener("resize", measure);
    vv?.addEventListener("scroll", measure);

    // Fallback: window resize
    window.addEventListener("resize", measure);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      vv?.removeEventListener("resize", measure);
      vv?.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  return (
    <>
      <AppBar
        ref={appBarRef}
        position="fixed"
        color="inherit"
        sx={{
          top: 0,
          right: 0,
          px: 2,
          py: 1,
          borderWidth: 1,
          borderColor: "grey.200",
          borderStyle: "solid",
          background: "background.paper",
          zIndex: 1100,
          ...sx,
        }}
        role="heading"
      >
        <Stack direction="row" alignItems="center" sx={{ flexGrow: 1 }} spacing={2}>
          {navBurger && <HeaderBarBurger />}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ flexGrow: 1 }}
          >
            {children}
          </Stack>
        </Stack>
      </AppBar>

      {/* Spacer dynamically matches AppBar height */}
      <Box sx={{ height: appBarHeight }} />
    </>
  );
};
